import { ErrorWithStatus } from '@kaiizen/zod-less-api';

/**
 * Poll a long-running backend job until it reaches a terminal state.
 *
 * Calls `start()` to fetch the current job state, then sleeps for
 * `pollIntervalMs` and repeats until `isTerminal(job)` returns true, the
 * elapsed time exceeds `maxPollTimeMs`, or the AbortSignal fires.
 *
 * Returns the terminal job. Throws on timeout or abort. Lets any error from
 * `start()` propagate — the caller (typically `useCachedQuery`'s requestWithRetry
 * layer) decides whether to retry.
 *
 * Failure-vs-success of the terminal job is the caller's concern: this helper
 * just polls until the job stops changing.
 */
export type PollAsyncJobOptions<T> = {
  /** Fetch the current state of the job. Called once per poll cycle. */
  start: () => Promise<T>;
  /** Predicate that returns true when the job has reached a terminal state. */
  isTerminal: (job: T) => boolean;
  /** Delay between poll cycles. Default: 1000ms. */
  pollIntervalMs?: number;
  /** Maximum total time to spend polling. Default: 5 min. */
  maxPollTimeMs?: number;
  /** Aborts the poll loop mid-flight. */
  signal?: AbortSignal;
  jobName: string;
};

const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_POLL_TIME_MS = 5 * 60 * 1000;

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ErrorWithStatus('Request aborted', 'aborted'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new ErrorWithStatus('Request aborted', 'aborted'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const pollAsyncJob = async <T>({
  start,
  isTerminal,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxPollTimeMs = DEFAULT_MAX_POLL_TIME_MS,
  signal,
  jobName,
}: PollAsyncJobOptions<T>): Promise<T> => {
  const startedAt = Date.now();
  while (true) {
    console.log(`pollAsyncJob: polling ${jobName}...`);
    if (signal?.aborted) throw new ErrorWithStatus(`${jobName} aborted`, 'aborted');
    const job = await start();
    if (isTerminal(job)) return job;
    console.log(`pollAsyncJob: ${jobName} is not terminal, waiting...`);
    if (Date.now() - startedAt > maxPollTimeMs) {
      throw new Error(`${jobName} time limit exceeded (${maxPollTimeMs}ms).`);
    }
    await wait(pollIntervalMs, signal);
  }
};
